import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { TYPE, MEASURE, SURFACE } from '../ui/tokens';

/**
 * The assistant's reply, rendered as markdown.
 *
 * It used to go into a `whitespace-pre-wrap` div, so a table the model wrote arrived on screen as
 * rows of literal `|` pipes and `---` dashes. That is the defect in the screenshots: the answer was
 * correct and the user was reading its source code.
 *
 * SAFETY. This text carries database content — product names, customer names, email subjects — all
 * of it ultimately typed by someone. So raw HTML is never rendered: react-markdown does not enable
 * it unless `rehype-raw` is added, and it deliberately is not. That is a stronger guarantee than
 * sanitising afterwards, because there is no HTML parse step to get wrong in the first place. No
 * raw-HTML injection API is used here either — see the ratchet in UntrustedHtmlFrame.test.tsx,
 * which scans every component for one and which this file must keep passing.
 *
 * Tables route through the same visual language as structured results, so an answer looks the same
 * whether the rows came back as data or as markdown the model wrote.
 */

export type MarkdownContentProps = {
  children: string;
  /** Constrain running text to a readable measure. Off inside a narrow container. */
  constrain?: boolean;
  className?: string;
};

/** An internal app path — routed, not a page load. Anything else is external. */
function isInternal(href?: string): boolean {
  return Boolean(href && href.startsWith('/') && !href.startsWith('//'));
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  children,
  constrain = true,
  className = '',
}) => {
  const text = String(children ?? '').trim();
  if (!text) return null;

  return (
    <div className={[TYPE.body, 'text-slate-800', constrain ? MEASURE : '', className].filter(Boolean).join(' ')}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children: c }) => <p className="mb-2 last:mb-0">{c}</p>,
          h1: ({ children: c }) => <h3 className={`${TYPE.title} text-slate-900 mt-3 first:mt-0 mb-1.5`}>{c}</h3>,
          h2: ({ children: c }) => <h4 className={`${TYPE.heading} text-slate-900 mt-3 first:mt-0 mb-1`}>{c}</h4>,
          h3: ({ children: c }) => <h5 className={`${TYPE.heading} text-slate-800 mt-2.5 first:mt-0 mb-1`}>{c}</h5>,
          ul: ({ children: c }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{c}</ul>,
          ol: ({ children: c }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{c}</ol>,
          li: ({ children: c }) => <li className="pl-0.5">{c}</li>,
          strong: ({ children: c }) => <strong className="font-semibold text-slate-900">{c}</strong>,
          em: ({ children: c }) => <em className="italic">{c}</em>,
          code: ({ children: c }) => (
            <code className="rounded bg-slate-100 border border-slate-200 px-1 py-0.5 text-[12.5px] font-mono">
              {c}
            </code>
          ),
          pre: ({ children: c }) => (
            <pre className="overflow-x-auto rounded-lg bg-slate-900 text-slate-100 p-3 mb-2 text-[12.5px]">{c}</pre>
          ),
          blockquote: ({ children: c }) => (
            <blockquote className="border-l-2 border-slate-300 pl-3 text-slate-600 mb-2">{c}</blockquote>
          ),
          hr: () => <hr className="my-3 border-slate-200" />,

          // Links. Internal paths go through the router so the app does not reload; external ones
          // carry rel="noopener noreferrer", because the reply can quote a supplier's own URL.
          a: ({ href, children: c }) => (isInternal(href)
            ? <Link to={href as string} className={`${SURFACE.brand} underline underline-offset-2 hover:no-underline`}>{c}</Link>
            : (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${SURFACE.brand} underline underline-offset-2 hover:no-underline`}
              >
                {c}
              </a>
            )),

          // A markdown table gets the same treatment as a data table: its own horizontal scroll,
          // a distinct header, and figures that line up.
          table: ({ children: c }) => (
            <div className="w-full overflow-x-auto border border-slate-200 rounded-lg mb-2 not-prose">
              <table className="w-full border-collapse text-left">{c}</table>
            </div>
          ),
          thead: ({ children: c }) => <thead className="bg-slate-50">{c}</thead>,
          tbody: ({ children: c }) => <tbody>{c}</tbody>,
          tr: ({ children: c }) => <tr className="border-b border-slate-100 last:border-b-0">{c}</tr>,
          th: ({ children: c, style }) => (
            <th
              scope="col"
              className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap"
              style={style}
            >
              {c}
            </th>
          ),
          // GFM carries per-column alignment in `style.textAlign`; a right-aligned column is a
          // numeric one, so it gets tabular figures and its digits line up.
          td: ({ children: c, style }) => {
            const right = style?.textAlign === 'right';
            return (
              <td
                className={[
                  'px-3 py-2 align-top text-slate-800',
                  TYPE.cell,
                  right ? `text-right ${TYPE.figure}` : '',
                ].filter(Boolean).join(' ')}
                style={style}
              >
                {c}
              </td>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
